import { callExternalApi } from "./external-api.service.js";
import { randomUuid } from "@/utils/uuid";
import { secureApiCallTemplate } from "./workspace.service.js";

const API_BASE_URL = import.meta.env.VITE_API_SERVER_URL;

/**
 * Share management (authenticated endpoints)
 */

// Create a new share link
export const createShareLink = async (
  workspaceUUID,
  audioFileUUID,
  options = {},
) => {
  const config = {
    url: `${API_BASE_URL}/workspaces/${workspaceUUID}/audio-files/${audioFileUUID}/shares`,
    method: "POST",
    data: options,
  };

  return await secureApiCallTemplate(config, true);
};

// Get all share links for an audio file
export const getShareLinks = async (workspaceUUID, audioFileUUID) => {
  const config = {
    url: `${API_BASE_URL}/workspaces/${workspaceUUID}/audio-files/${audioFileUUID}/shares`,
    method: "GET",
  };

  return await secureApiCallTemplate(config);
};

// Update a share link
export const updateShareLink = async (
  workspaceUUID,
  audioFileUUID,
  shareLinkUUID,
  updates,
) => {
  const config = {
    url: `${API_BASE_URL}/workspaces/${workspaceUUID}/audio-files/${audioFileUUID}/shares/${shareLinkUUID}`,
    method: "PUT",
    data: updates,
  };

  return await secureApiCallTemplate(config, true);
};

// Delete a share link
export const deleteShareLink = async (
  workspaceUUID,
  audioFileUUID,
  shareLinkUUID,
) => {
  const config = {
    url: `${API_BASE_URL}/workspaces/${workspaceUUID}/audio-files/${audioFileUUID}/shares/${shareLinkUUID}`,
    method: "DELETE",
  };

  return await secureApiCallTemplate(config, true);
};

// Get share analytics
export const getShareAnalytics = async (
  workspaceUUID,
  audioFileUUID,
  shareLinkUUID,
) => {
  const config = {
    url: `${API_BASE_URL}/workspaces/${workspaceUUID}/audio-files/${audioFileUUID}/shares/${shareLinkUUID}/analytics`,
    method: "GET",
  };

  return await secureApiCallTemplate(config);
};

/**
 * Public access (no authentication required)
 */

// Get public share data
export const getPublicShare = async (shareLinkUUID) => {
  const config = {
    url: `${API_BASE_URL}/share/${shareLinkUUID}`,
    method: "GET",
  };

  return await callExternalApi({ config });
};

// One pageview id per page load: the server dedupes share events per
// (link, type, pageviewId), so concurrent viewers behind one NAT all count
// while reloads/spam collapse (design: share-telemetry-and-rate-limit.md §D).
let pageviewId = null;
export const getPageviewId = () => {
  if (!pageviewId) {
    pageviewId = randomUuid(); // null when no crypto — id then omitted
  }
  return pageviewId;
};
// Test hook only.
export const _resetPageviewId = () => {
  pageviewId = null;
};

// Track share events
export const trackShareEvent = async (
  shareLinkUUID,
  eventType,
  eventData = {},
  { accessToken } = {},
) => {
  const config = {
    url: `${API_BASE_URL}/share/${shareLinkUUID}/events`,
    method: "POST",
    data: {
      eventType,
      eventData: {
        ...eventData,
        ...(getPageviewId() ? { pageviewId: getPageviewId() } : {}),
      },
    },
    ...(accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : {}),
  };

  return await callExternalApi({ config, skipCache: true });
};

// Click-time SIGNED_UP, fired immediately before the Auth0 redirect: uses
// keepalive fetch so the navigation cannot cancel it, with an explicit JSON
// content type (the API 415s anything else). Fire-and-forget by design.
export const trackShareSignupClickBeacon = (shareLinkUUID) => {
  try {
    fetch(`${API_BASE_URL}/share/${shareLinkUUID}/events`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: "SIGNED_UP",
        eventData: getPageviewId() ? { pageviewId: getPageviewId() } : {},
      }),
    }).catch(() => {});
  } catch {
    // Never let telemetry break signup.
  }
};

/**
 * Download functions (authentication required)
 */

// Download shared audio file
export const downloadSharedAudio = async (shareLinkUUID) => {
  const config = {
    url: `${API_BASE_URL}/share/${shareLinkUUID}/download/audio`,
    method: "GET",
  };

  return await secureApiCallTemplate(config, true);
};

// Download shared transcript
export const downloadSharedTranscript = async (
  shareLinkUUID,
  format = "txt",
) => {
  const config = {
    url: `${API_BASE_URL}/share/${shareLinkUUID}/download/transcript?format=${format}`,
    method: "GET",
    responseType: "blob",
  };

  return await secureApiCallTemplate(config, true);
};

// Download shared summary
export const downloadSharedSummary = async (shareLinkUUID, format = "txt") => {
  const config = {
    url: `${API_BASE_URL}/share/${shareLinkUUID}/download/summary?format=${format}`,
    method: "GET",
    responseType: "blob",
  };

  return await secureApiCallTemplate(config, true);
};

/**
 * Utility functions
 */

// Generate public share URL
export const generateShareUrl = (shareLinkUUID) => {
  const baseUrl = globalThis.location.origin;
  return `${baseUrl}/share/${shareLinkUUID}`;
};

// Copy share URL to clipboard
export const copyShareUrl = async (shareLinkUUID) => {
  const shareUrl = generateShareUrl(shareLinkUUID);

  try {
    await navigator.clipboard.writeText(shareUrl);
    return { success: true, url: shareUrl };
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = shareUrl;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    return { success: true, url: shareUrl };
  }
};
